import React, { useEffect, useState, ChangeEvent, useRef } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
} from 'reactstrap';
import { useUser } from 'context/UserContext';
import { useAlertPlus, useConfirm } from 'hooks';
import Required from 'components/Required';
import { DatePickerChangeEvent } from 'components/DatePicker';
import ConfirmModal from 'components/ConfirmModal';
import AlertModal from 'components/AlertModal';
import UploaderMultiple from 'components/UploaderMultiple';
import CuitInput from 'components/CuitInput';
import HonorariosEntidades from 'components/MatriculateRequest/HonorariosEntidades';
import { useHistory } from 'react-router-dom';
import { getAllConsultoriosCokiba, postConsultorio } from '../../features/consultorios/consultoriosAPI';
import { Consultorio, ConsultorioCokiba, HorariosAtencionRegistrado } from 'types/Consultorio';
import { horariosRegistradosFromConsultorios, validarSolapamientos } from '../../utils/horariosValidacion';
import { getPartidos } from '../../features/partidos/partidosAPI';
import { Partidos } from 'types/Partidos';
import { getLocalidades } from '../../features/localidades/localidadesAPI';
import { Localidades } from 'types/Localidades';
import HorariosAtencionComponent from 'components/Consultorios/HorariosAtencion';
import { useTotp } from 'hooks/useTotp';

export default function CreateConsultorio() {
  const { getMatricula } = useUser();
  const matriculateId = getMatricula();
  if (!matriculateId) {
    return null;
  }

  const history = useHistory();

  const [formValidation, setFormValidation] = useState<
    Partial<Record<keyof Consultorio, boolean>>
  >({});

  const [disableSend, setDisableSend] = useState(false);

  const [isLoading, setLoading] = useState(true);

  const [showAlert, message, alertCallback, alert] = useAlertPlus('');
  const [confirm, confirmTitle, confirmAction, setConfirm] = useConfirm(false);

  const [partidos, setPartidos] = useState<Partidos[]>([]);
  const [localidades, setLocalidades] = useState<Localidades[]>([]);

  const formElement = useRef<HTMLFormElement | null>(null);

  const [data, setData] = useState<Consultorio>({
    id: 0,
    state_id: null,
    matriculado: matriculateId,
    nombre: '',
    calle: '',
    numero: '',
    piso: '',
    departamento: '',
    localidad: 0,
    partido: 0,
    codigo_postal: '',
    prefijo_telefono: '',
    telefono: '',
    tipo_propietario: '',
    nombre_propietario: '',
    cuit_propietario: '',
    motivo_rechazo: '',
    state: null,
    attachs: [],
    horarios_atencion: [],
  });

  const [horariosAtencion, setHorariosAtencion] = useState<HorariosAtencionRegistrado[]>([]);

  const { requestTotp, TotpModal } = useTotp();

  useEffect(() => {
    setLoading(true);

    const loadData = async () => {
      try {
        const provinciaId = 1; // Buenos Aires
        const partidosResponse = await getPartidos(provinciaId);
        if (partidosResponse) {
          setPartidos(partidosResponse);
        }

        const consultorios: ConsultorioCokiba[] = await getAllConsultoriosCokiba() as ConsultorioCokiba[];
        if (consultorios) {
          setHorariosAtencion(horariosRegistradosFromConsultorios(consultorios));
        }
      } catch (err) {
        setDisableSend(true);
        alert(
          'Se produjo un error al intentar recuperar información inicial. ' +
            'Por favor, intente nuevamente más tarde.',
        );
        console.error('ConsultoriosCreate', 'Error al cargar datos iniciales');
        console.info(err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    setLoading(true);

    const loadLocalidades = async () => {
      try {
        const localidadesResponse = await getLocalidades(data.partido);
        if (localidadesResponse) {
          setLocalidades(localidadesResponse);
        }
      } catch (err) {
        setDisableSend(true);
        alert(
          'Se produjo un error al intentar recuperar las localidades. ' +
            'Por favor, intente nuevamente más tarde.',
        );
        console.error('ConsultoriosCreate', 'Error al cargar las localidades');
        console.info(err);
      } finally {
        setLoading(false);
      }
    };

    loadLocalidades();
  }, [data.partido]);

  const validarHorarios = (): boolean => {
    if (data.horarios_atencion.length === 0) {
      alert(`Se debe cargar al menos un día y horarios de atención.`);
      return false;
    }

    const resultado = validarSolapamientos(data.horarios_atencion, horariosAtencion);
    if (!resultado.ok) {
      alert(resultado.message ?? 'Hay horarios superpuestos.');
      return false;
    }

    return true;
  };

  const handleFormValidation = () => {
    let newFormValidation = {};

    let toValidate = [
      ...Array.from(document.getElementsByClassName('form-control')),
    ] as HTMLInputElement[];
    toValidate = toValidate.filter(e => !e.disabled && e.required);

    let missing = false;
    // Validar campos required
    toValidate.forEach(field => {
      if (field.required && !field.disabled && !field.value?.trim()) {
        field.classList.add('is-invalid');

        missing = true;
      } else if (field.inputMode === 'numeric') {
        if (!/^\d+$/.test(field.value)) {
          newFormValidation = {
            ...newFormValidation,
            [field.name]: false,
          };
        }
      } else {
        field.classList.remove('is-invalid');
      }
    });

    if (missing) {
      setFormValidation(newFormValidation);
      alert(
        'Todos los campos marcados con * son obligatorios. ' +
          'Por favor complete los faltantes antes de enviar la solicitud.',
      );
      return false;
    }

    // Si los campos estan bien, se revisan los horarios
    return validarHorarios();
  };

  // Confirmación de envío de Solicitud
  const handleSendForm = () => {
    if (!handleFormValidation()) {
      return;
    }

    setConfirm(
      true,
      '¿Estás seguro que deseas enviar esta solicitud de consultorio?',
      'send',
    );
  };

  // Gestor de respuestas de confirmaciones
  const handleConfirmation = (action: string) => {
    if (action === 'send') {
      handleSend();
    }

    setConfirm(false);
  };

  // Envío de solicitudes
  const handleSend = async () => {
    setFormValidation({});

    setLoading(true);

    // Preparamos los inputs menos los archivos
    const consultorio: Partial<Consultorio> = {};

    if (!formElement.current) return;

    new FormData(formElement.current).forEach((value, key) => {
      // @ts-ignore
      consultorio[key as keyof Consultorio] = value.toString() as any;
    });

    // Seteamos los horarios
    consultorio['horarios_atencion'] = data.horarios_atencion;

    // Preparamos los archivos
    const files: Record<string, File> = {};
    const inputs = Array.from(document.querySelectorAll('input[type="file"]'));
    for (const inputElement of inputs) {
      const input = inputElement as HTMLInputElement;
      if (input.hasAttribute('required') && input.value === '') {
        alert(
          'Faltan subir archivos requeridos para poder enviar la solicitud.',
        );
        setLoading(false);
        return;
      }

      if (input.files) {
        for (let i = 0; i < input.files.length; i++) {
          files[`${input.name}_${i}`] = input.files[i];
        }
      }
    }

    const code = await requestTotp('consultorio');
    if (!code) return;

    // Enviamos la petición
    await postConsultorio(consultorio, files, code)
      .then(res => {
        if (!res || res.status != 'ok') {
          // Error interno
          console.error('ConsultoriosCreate', 'Error al enviar la solicitud');
          console.info(res);
          alert(
            res.message ??
            'Ha ocurrido un error al enviar la solicitud. Por favor intente nuevamente.',
          );
          return;
        }

        alert('La solicitud se envió correctamente', () => {
          history.push('/consultorios');
        });
      })
      .catch(err => {
        console.error(
          'ConsultoriosCreate',
          'Error inesperado al intentar enviar la solicitud',
        );
        console.info(err);
        alert(
          'Ha ocurrido un error al enviar la solicitud. Por favor intente nuevamente.',
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleInputChange = (
    event: ChangeEvent<HTMLInputElement> | DatePickerChangeEvent,
  ) => {
    const { target } = event;

    const name = target.name;

    let value: string | number | Date | null;
    value = target.value;
    if (
      typeof value === 'string' &&
      ['dni', 'cuit', 'telefono', 'legal_telefono', 'legal_celular'].includes(
        name,
      )
    ) {
      value = !!parseInt(value) ? parseInt(value) : '';
    }

    // Guardamos el nuevo valor
    setData({
      ...data,
      [name]: value,
    });
  };

  return (
    <div className="content">
      <Row>
        <Col sm={12}>
          <Card>
            <CardHeader>
              <Row style={{ alignItems: 'center' }}>
                <Col sm={8}>
                  <CardTitle tag="h5">Nuevo Consultorio</CardTitle>
                </Col>
                <Col sm={4} className="text-right">
                  <Button
                    className="btn-round"
                    color="primary"
                    onClick={() => history.push('/consultorios')}
                  >
                    Volver
                  </Button>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              <Form innerRef={formElement}>
                <Col sm={12}>
                  <CardTitle>Dirección</CardTitle>
                </Col>
                <FormGroup>
                  <Row>
                    <Col sm={6}>
                      <Label for="nombre">
                        Nombre <Required />
                      </Label>
                      <Input
                        id="nombre"
                        name="nombre"
                        value={data.nombre ?? ''}
                        invalid={formValidation.nombre === false}
                        onChange={handleInputChange}
                        disabled={isLoading}
                        required
                      />
                    </Col>
                    <Col sm={6}>
                      <Label for="codigo_postal">
                        Código postal <Required />
                      </Label>
                      <Input
                        id="codigo_postal"
                        name="codigo_postal"
                        value={data.codigo_postal ?? ''}
                        invalid={formValidation.codigo_postal === false}
                        onChange={handleInputChange}
                        disabled={isLoading}
                        required
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <Row>
                    <Col sm={6}>
                      <Label for="calle">
                        Calle <Required />
                      </Label>
                      <Input
                        id="calle"
                        name="calle"
                        value={data.calle ?? ''}
                        invalid={formValidation.calle === false}
                        onChange={handleInputChange}
                        disabled={isLoading}
                        required
                      />
                    </Col>
                    <Col sm={6}>
                      <Label for="numero">
                        Número <Required />
                      </Label>
                      <Input
                        id="numero"
                        name="numero"
                        value={data.numero ?? ''}
                        invalid={formValidation.numero === false}
                        onChange={handleInputChange}
                        disabled={isLoading}
                        required
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <Row>
                    <Col sm={6}>
                      <Label for="piso">Piso</Label>
                      <Input
                        id="piso"
                        name="piso"
                        value={data.piso ?? ''}
                        invalid={formValidation.piso === false}
                        onChange={handleInputChange}
                        disabled={isLoading}
                      />
                    </Col>
                    <Col sm={6}>
                      <Label for="departamento">Departamento</Label>
                      <Input
                        id="departamento"
                        name="departamento"
                        value={data.departamento ?? ''}
                        invalid={formValidation.departamento === false}
                        onChange={handleInputChange}
                        disabled={isLoading}
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <Row>
                    <Col md="6">
                      <Label for="partido">
                        Partido <Required />
                      </Label>
                      <Input
                        id="partido"
                        name="partido"
                        type="select"
                        className="custom-select"
                        value={data.partido ?? ''}
                        invalid={formValidation.partido === false}
                        onChange={handleInputChange}
                        disabled={isLoading}
                        required
                      >
                        <option value="">Seleccione una opción...</option>
                        {partidos?.map(item => {
                          return (
                            <option
                              key={item.MTR038_ID_PARTIDO}
                              value={item.MTR038_ID_PARTIDO}
                            >
                              {item.MTR038_D_PARTIDO}
                            </option>
                          );
                        })}
                      </Input>
                    </Col>
                    <Col md="6">
                      <Label for="localidad">
                        Localidad <Required />
                      </Label>
                      <Input
                        id="localidad"
                        name="localidad"
                        type="select"
                        className="custom-select"
                        value={data.localidad ?? ''}
                        invalid={formValidation.localidad === false}
                        onChange={handleInputChange}
                        disabled={isLoading}
                        required
                      >
                        <option value="">Seleccione una opción...</option>
                        {localidades?.map(item => {
                          return (
                            <option
                              key={item.MTR008_ID_LOCALIDAD}
                              value={item.MTR008_ID_LOCALIDAD}
                            >
                              {item.MTR008_D_LOCALIDAD}
                            </option>
                          );
                        })}
                      </Input>
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <Row>
                    <Col sm={6}>
                      <Label for="prefijo_telefono">
                        Prefijo <Required />
                      </Label>
                      <Input
                        id="prefijo_telefono"
                        name="prefijo_telefono"
                        value={data.prefijo_telefono ?? ''}
                        invalid={formValidation.prefijo_telefono === false}
                        onChange={handleInputChange}
                        disabled={isLoading}
                        required
                      />
                    </Col>
                    <Col sm={6}>
                      <Label for="telefono">
                        Teléfono <Required />
                      </Label>
                      <Input
                        id="telefono"
                        name="telefono"
                        value={data.telefono ?? ''}
                        invalid={formValidation.telefono === false}
                        onChange={handleInputChange}
                        disabled={isLoading}
                        required
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <hr className="my-4" />
                <Col sm={12}>
                  <CardTitle>Propietario</CardTitle>
                </Col>
                <FormGroup>
                  <Row>
                    <Col md="6">
                      <Label for="tipo_propietario">
                        Tipo de Persona <Required />
                      </Label>
                      <Input
                        id="tipo_propietario"
                        name="tipo_propietario"
                        type="select"
                        className="custom-select"
                        value={data.tipo_propietario ?? ''}
                        invalid={formValidation.tipo_propietario === false}
                        onChange={handleInputChange}
                        disabled={isLoading}
                        required
                      >
                        <option value="">Seleccione una opción...</option>
                        <option key={'FISICA'} value={'FISICA'}>Persona Física</option>
                        <option key={'JURIDICA'} value={'JURIDICA'}>Persona Jurídica</option>
                      </Input>
                    </Col>
                    <Col sm={6}>
                      <Label for="nombre_propietario">
                        {data.tipo_propietario == 'JURIDICA' ? 'Razón Social' : 'Nombre y Apellido'} <Required />
                      </Label>
                      <Input
                        id="nombre_propietario"
                        name="nombre_propietario"
                        value={data.nombre_propietario ?? ''}
                        invalid={formValidation.nombre_propietario === false}
                        onChange={handleInputChange}
                        disabled={isLoading}
                        required
                      />
                    </Col>
                    <Col md="6"></Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <Row>
                    <Col sm={6}>
                      <Label for="cuit_propietario">
                        CUIT <Required />
                      </Label>
                      <CuitInput
                        id="cuit_propietario"
                        name="cuit_propietario"
                        value={data.cuit_propietario ?? ''}
                        invalid={formValidation.cuit_propietario === false}
                        onChange={handleInputChange}
                        disabled={isLoading}
                        required
                      />
                    </Col>
                    <Col md="6"></Col>
                  </Row>
                </FormGroup>
                <hr className="my-4" />
                <Col sm={12}>
                  <CardTitle>Atención</CardTitle>
                </Col>
                <FormGroup>
                  <Row className="mt-4 mb-2">
                    <Col md="6">
                      <Label for="documentacion">
                        Días y horarios de atención <Required />
                      </Label>
                      <HorariosAtencionComponent
                        value={data.horarios_atencion}
                        actuales={horariosAtencion}
                        onChange={horarios =>
                          setData({ ...data, horarios_atencion: horarios })
                        }
                        disabled={isLoading}
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <hr className="my-4" />
                <Col sm={12}>
                  <CardTitle>Adjuntos</CardTitle>
                </Col>
                <Col sm={12}>
                  <HonorariosEntidades
                    titulosPagos={[
                      'TASA_MINISTERIO_SALUD_BS_AS',
                      'ARANCEL_INSPECCION_COKIBA',
                      'ARBA_TIMBRADO_EN_BLANCO',
                      'ARBA_TASA_ADMINISTRATIVA',
                    ]}
                  />
                </Col>
                <FormGroup>
                  <Row className="mt-4 mb-2">
                    <Col md="6">
                      <Label for="constancia_pagos">
                        Constancia de haber realizado los 4 pagos detallados anteriormente <Required />
                      </Label><br/>
                      <UploaderMultiple name="constancia_pagos" required multiple />
                    </Col>
                    <Col md="6">
                      <Label for="solicitud_habilitacion">
                        Nota Tipo 1 solicitando la habilitación <Required />
                      </Label><br/>
                      <UploaderMultiple name="solicitud_habilitacion" required />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <Row className="mt-4 mb-2">
                    <Col md="6">
                      <Label for="declaracion_aparatologia">
                        Declaración Jurada de aparatología{' '}
                        <Required />
                      </Label><br/>
                      <UploaderMultiple
                        name="declaracion_aparatologia"
                        required
                        accept={[
                          'image/jpeg',
                          'image/png',
                          'application/pdf',
                          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                          'application/msword',
                        ]}
                      />
                    </Col>
                    <Col md="6">
                      <Label for="declaracion_patogenicos">
                        Declaración Jurada de NO generación de residuos patogénicos <Required />
                      </Label><br/>
                      <UploaderMultiple
                        name="declaracion_patogenicos"
                        required
                        accept={[
                          'application/pdf',
                          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                          'application/msword',
                        ]}
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <Row className="mt-4 mb-2">
                    <Col md="6">
                      <Label for="plano_municipal">
                        Plano Municipal aprobado. En caso de ser fotocopia debe estar legalizada <Required />
                      </Label><br/>
                      <UploaderMultiple
                        name="plano_municipal"
                        required
                        accept={[
                          'image/jpeg',
                          'image/png',
                          'application/pdf',
                          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                          'application/msword',
                        ]}
                        multiple
                      />
                    </Col>
                    <Col md="6">
                      <Label for="contrato_alquiler">
                        Contrato de alquiler, contrato de comodato o escritura, a nombre{' '}
                          de quien será el propietario del consultorio <Required />
                      </Label><br/>
                      <UploaderMultiple
                        name="contrato_alquiler"
                        required
                        accept={[
                          'application/pdf',
                          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                          'application/msword',
                        ]}
                        multiple
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <Row className="mt-4 mb-2">
                    <Col md="6">
                      <Label for="titulos_grado">
                        Título de grado <Required />
                      </Label><br/>
                      <UploaderMultiple name="titulos_grado" required multiple />
                    </Col>
                    <Col md="6">
                      <Label for="constancia_cuit">
                        Constancia de CUIT de todos los profesionales de la kinesiología que{' '}
                          se mencionan en la nota de solicitud <Required />
                      </Label><br/>
                      <UploaderMultiple name="constancia_cuit" required multiple />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <Row className="mt-4 mb-2">
                    <Col md="6">
                      <Label for="croquis_consultorio">
                        Croquis y memoria descriptiva del consultorio o establecimiento, con firma{' '}
                          y sello del director técnico responsable <Required />
                      </Label><br/>
                      <UploaderMultiple
                        name="croquis_consultorio"
                        required
                        accept={[
                          'image/jpeg',
                          'image/png',
                          'application/pdf',
                          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                          'application/msword',
                        ]}
                      />
                    </Col>
                    <Col md="6">
                      <Label for="contrato_comodato">
                        Escritura de contrato de comodato <Required />
                      </Label><br/>
                      <UploaderMultiple
                        name="contrato_comodato"
                        required
                        accept={[
                          'application/pdf',
                          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                          'application/msword',
                        ]}
                        multiple
                      />
                    </Col>
                  </Row>
                </FormGroup>
                {data.tipo_propietario == 'JURIDICA'
                  ?
                    <FormGroup>
                      <Row className="mt-4 mb-2">
                        <Col md="6">
                          <Label for="estatuto_societario">
                            Estatuto Societario, con la correspondiente inscripción en la Inspección General de Justicia
                            o en la Dirección Provincial de Personas Jurídicas.
                            Si envía fotocopia debe estar legalizada <Required />
                          </Label><br/>
                          <UploaderMultiple
                            name="estatuto_societario"
                            required
                            accept={['application/pdf']}
                            multiple
                          />
                        </Col>
                        <Col md="6"></Col>
                      </Row>
                    </FormGroup>
                  : null
                }
                <FormGroup>
                  <div className="text-center">
                    <Button
                      type="button"
                      className="btn-round"
                      onClick={handleSendForm}
                      disabled={isLoading || disableSend}
                    >
                      Crear
                    </Button>
                  </div>
                </FormGroup>
              </Form>
              <AlertModal
                isOpen={showAlert}
                message={message}
                onClose={alertCallback}
              />
              <ConfirmModal
                isOpen={confirm}
                title={confirmTitle}
                action={confirmAction}
                onClose={() => setConfirm(false)}
                onConfirm={handleConfirmation}
              />
              {TotpModal}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
